import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabaseClient } from '../../lib/supabase';
import { LoadingScreen } from '../common/LoadingScreen';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from '../../lib/toast';

interface ProjectStats {
  id: string;
  name: string;
  totalPages: number;
  lowScore: number;
  mediumScore: number;
  highScore: number;
}

export function ProjectStats() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [projectStats, setProjectStats] = useState<ProjectStats[]>([]);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Get user role
  const userRole = 
    user?.user_metadata?.role || 
    user?.role ||
    (user?.email === 'andmaillev@gmail.com' ? 'admin' : 'client');

  useEffect(() => {
    const loadProjectStats = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Get all projects with analyses and user data
        const { data: projects, error: projectError } = await supabaseClient
          .from('projects')
          .select(`
            id,
            name,
            owner_id,
            analyses (
              id,
              text_score
            ),
            project_users (
              user_id,
              role
            )
          `);

        if (projectError) throw projectError;

        // Filter projects based on user role and access
        const filteredProjects = projects?.filter(project => {
          // Admins can see all projects
          if (userRole === 'admin' || user?.email === 'andmaillev@gmail.com') {
            return true;
          }

          // Optimizers can see projects they created or where they are assigned
          if (userRole === 'optimizer') {
            return project.owner_id === user?.id || 
                   project.project_users?.some(pu => 
                     pu.user_id === user?.id && pu.role === 'optimizer'
                   );
          }

          // Clients can only see projects where they are assigned
          if (userRole === 'client') {
            return project.project_users?.some(pu => 
              pu.user_id === user?.id
            );
          }

          return false;
        });

        // Calculate statistics for each project
        const stats = filteredProjects?.map(project => {
          const analyses = project.analyses || [];
          return {
            id: project.id,
            name: project.name,
            totalPages: analyses.length,
            lowScore: analyses.filter(a => a.text_score !== null && a.text_score < 50).length,
            mediumScore: analyses.filter(a => a.text_score !== null && a.text_score >= 50 && a.text_score < 75).length,
            highScore: analyses.filter(a => a.text_score !== null && a.text_score >= 75).length
          };
        }) || [];

        setProjectStats(stats);
        setError(null);
      } catch (err) {
        console.error('Error loading project stats:', err);
        setError('Failed to load project statistics. Please try refreshing the page.');
        toast.error('Failed to load project statistics');
      } finally {
        setLoading(false);
      }
    };

    loadProjectStats();
  }, [user, userRole]);

  if (loading) return <LoadingScreen />;

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h1 className="text-2xl font-bold mb-6">Project Statistics</h1>

      {error && (
        <div className="mb-4 p-4 text-sm text-red-700 bg-red-100 rounded-md">
          {error}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Project Name
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total Pages
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Score 1-49
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Score 50-74
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Score 75-100
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {projectStats.map((project) => (
              <tr key={project.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => navigate(`/projects/show/${project.id}`)}
                    className="text-blue-600 hover:text-blue-800 hover:underline text-left"
                  >
                    {project.name}
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900">
                  {project.totalPages}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                  <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                    {project.lowScore}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                  <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                    {project.mediumScore}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                  <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                    {project.highScore}
                  </span>
                </td>
              </tr>
            ))}
            {projectStats.length === 0 && (
              <tr>
                <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500">
                  No projects found
                </td>
              </tr>
            )}
          </tbody>
          <tfoot className="bg-gray-50">
            <tr>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                Total
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900">
                {projectStats.reduce((sum, p) => sum + p.totalPages, 0)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                  {projectStats.reduce((sum, p) => sum + p.lowScore, 0)}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                  {projectStats.reduce((sum, p) => sum + p.mediumScore, 0)}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                  {projectStats.reduce((sum, p) => sum + p.highScore, 0)}
                </span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}